import { Component, EventEmitter, Input, OnInit, Output, inject, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ToastService } from 'angular-toastify';
import { ParkingTransactionDetailResponse, ParkingTransactionResponse, ParkingGateListResponse, ParkingTransactionPayload } from '../../../../../../core/models/parking.model';
import { DiscountAvailableStamp } from '../../../../../../core/models/parking/discount-apply.model';
import { ApiService } from '../../../../../../core/services/api.service';
import { ApiDiscountApplyService } from '../../../../../../core/services/api/parking/api-discount-apply.service';
import { MapperDiscountApplyService } from '../../../../../../core/mappers/parking/mapper-discount-apply.service';
import { MapperParkingMasterDataService } from '../../../../../../core/mappers/system-settings/mapper-parking-master-data.service';
import { animate, style, transition, trigger } from '@angular/animations';
import { ModalTransactionParkingMemberCardComponent } from '../modal-transaction-parking-member-card/modal-transaction-parking-member-card.component';
import { switchMap } from 'rxjs';
import { cityParkingVehicleMasterListResponse } from '../../../../../../core/models/lineOA.model';
import { environment } from '../../../../../../../environments/environment';
import { ModalAdminActivityHistoryComponent } from '../../../system-settings/modal-admin-activity-history/modal-admin-activity-history.component';
import { AdminActivityEntityTypes } from '../../../../../../core/models/system-settings/admin-activity-log.model';


@Component({
  selector: 'app-modal-transaction-parking-detail',
  standalone: true,
  imports: [CommonModule, ModalTransactionParkingMemberCardComponent, FormsModule, ModalAdminActivityHistoryComponent],
  templateUrl: './modal-transaction-parking-detail.component.html',
  styleUrl: './modal-transaction-parking-detail.component.css',
  animations: [
    trigger('backdropAnimation', [
      transition(':enter', [
        style({ opacity: 0 }),
        animate('300ms ease-out', style({ opacity: 1 }))
      ]),
      transition(':leave', [
        animate('200ms ease-in', style({ opacity: 0 }))
      ])
    ]),
    trigger('modalAnimation', [
      transition(':enter', [
        style({ transform: 'scale(0.95)', opacity: 0 }),
        animate('300ms cubic-bezier(0.16, 1, 0.3, 1)', style({ transform: 'scale(1)', opacity: 1 }))
      ]),
      transition(':leave', [
        animate('200ms ease-in', style({ transform: 'scale(0.95)', opacity: 0 }))
      ])
    ])
  ]
})
export class ModalTransactionParkingDetailComponent implements OnInit {

  @Input() parkingTransaction: ParkingTransactionResponse | null = null;
  @Input() userId: number = 0;
  @Output() close = new EventEmitter<void>();
  @Output() openImage = new EventEmitter<string>();

  readonly activityEntityTypes = AdminActivityEntityTypes;
  showActivityHistory = false;

  detail: ParkingTransactionDetailResponse | null = null;
  vehicleMaster: cityParkingVehicleMasterListResponse | null = null;

  //remark
  remark: string = '';

  // Gate Lists (read-only display)
  entryGateList: ParkingGateListResponse[] = [];
  exitGateList: ParkingGateListResponse[] = [];

  isLoading: boolean = false;
  error: string | null = null;

  // Member Card Modal State
  showMemberCardModal: boolean = false;

  // Stamp modal (reuse Discount GetAvailableStamps)
  showStampModal = signal(false);
  isLoadingStamps = signal(false);
  isApplyingStamp = signal(false);
  pickerStamps = signal<DiscountAvailableStamp[]>([]);
  selectedStampId = signal(0);
  stampSearchText = signal('');
  stampRemark = '';

  private readonly apiService = inject(ApiService);
  private readonly discountApi = inject(ApiDiscountApplyService);
  private readonly discountMapper = inject(MapperDiscountApplyService);
  private readonly actionMapper = inject(MapperParkingMasterDataService);
  private readonly toast = inject(ToastService);

  ngOnInit(): void {
    if (this.parkingTransaction) {
      this.loadDetail(this.parkingTransaction.id);
    }
  }

  reloadDetail(): void {
    if (!this.parkingTransaction?.id || this.isLoading) return;
    this.loadDetail(this.parkingTransaction.id);
  }

  loadDetail(id: number) {
    this.isLoading = true;
    this.error = null;
    this.detail = null;

    this.apiService.getVehicleMaster().pipe(
      switchMap((vehicleRes) => {
        this.vehicleMaster = vehicleRes;
        return this.apiService.getParkingTransactionDetail(id, this.userId);
      })
    ).subscribe({
      next: (res) => {
        this.detail = res;
        this.isLoading = false;

        if (this.detail?.siteId) {
          this.loadGateList(this.detail.siteId);
        }
      },
      error: (err) => {
        console.error("Error loading detail:", err);
        this.error = "ไม่สามารถโหลดข้อมูลได้";
        this.isLoading = false;
      }
    });
  }

  loadGateList(siteId: number) {
    this.apiService.getParkingGate(siteId).subscribe({
      next: (res) => {
        this.entryGateList = res.filter((g) => g.entry === true || (g as ParkingGateListResponse & { Entry?: boolean }).Entry === true);
        this.exitGateList = res.filter((g) => g.exit === true || (g as ParkingGateListResponse & { Exit?: boolean }).Exit === true);
      },
      error: (err) => console.error("Error loading gate list:", err)
    });
  }

  getEntryGateName(id: number | undefined): string {
    if (!id) return '-';
    const gate = this.entryGateList.find(g => g.id === id);
    return gate ? gate.name : String(id);
  }

  get filteredPickerStamps(): DiscountAvailableStamp[] {
    const term = this.stampSearchText().trim().toLowerCase();
    const list = this.pickerStamps();
    if (!term) return list;
    return list.filter((s) => {
      const haystack = [
        s.rateName,
        s.rateId,
        s.groupName,
        s.costType,
        s.parkingRateId != null ? String(s.parkingRateId) : '',
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();
      return haystack.includes(term);
    });
  }

  getExitGateName(id: number | undefined): string {
    if (!id) return '-';
    const gate = this.exitGateList.find(g => g.id === id);
    return gate ? gate.name : String(id);
  }

  onImageClick(url: string | undefined): void {
    if (url) {
      this.openImage.emit(url);
    }
  }

  onClose() {
    this.close.emit();
  }

  onSave() {
    if (!this.detail || !this.parkingTransaction) return;

    const payload: ParkingTransactionPayload = {
      id: this.parkingTransaction.id,
      entryGateId: this.detail.entryGateId,
      exitGateId: this.detail.exitGateId,
      remark: this.remark
    };

    this.isLoading = true;
    this.apiService.saveParkingTransaction(payload).subscribe({
      next: (res) => {
        this.isLoading = false;
        if (res.isSuccess) {
          this.onClose();
        } else {
          console.error('Save failed', res.message);
        }
      },
      error: (err) => {
        this.isLoading = false;
        console.error('Save error', err);
      }
    });
  }

  // --- New Logic ---

  openPayLink() {
    let hex = this.detail?.cardCode;
    // if (/^\d+$/.test(hex)) {
    //   hex = BigInt(hex).toString(16);
    // }
    window.open(`${environment.webPayUrl}/payment/invoice?data=${hex}`, '_blank');
    console.log("This data detail : ", this.detail)
  }

  openStampModal(): void {
    if (!this.parkingTransaction?.id) {
      this.toast.error('ไม่พบธุรกรรม');
      return;
    }
    if (!this.userId) {
      this.toast.error('ไม่พบผู้ใช้');
      return;
    }
    if (this.detail?.exitDate) {
      this.toast.error('รถออกจากลานแล้ว ไม่สามารถประทับตราได้');
      return;
    }
    if (!this.detail?.cardCode) {
      this.toast.error('ไม่พบ card code');
      return;
    }

    this.isLoadingStamps.set(true);
    this.discountApi.getAvailableStamps(this.userId, this.parkingTransaction.id).subscribe({
      next: (res) => {
        this.isLoadingStamps.set(false);
        const all = this.discountMapper.mapAvailableStampList(res);
        const filtered = all.filter((s) => !s.alreadyStamped && s.hasQuotaRemaining);
        if (filtered.length === 0) {
          this.toast.error('ไม่มีตราที่ใช้งานได้');
          return;
        }
        this.pickerStamps.set(filtered);
        this.selectedStampId.set(filtered[0]?.parkingRateId ?? 0);
        this.stampSearchText.set('');
        this.stampRemark = '';
        this.showStampModal.set(true);
      },
      error: (err) => {
        this.isLoadingStamps.set(false);
        this.toast.error(err?.error?.message || 'โหลดตราประทับไม่สำเร็จ');
      },
    });
  }

  closeStampModal(): void {
    this.showStampModal.set(false);
    this.stampSearchText.set('');
  }

  selectStamp(id: number): void {
    this.selectedStampId.set(id);
  }

  confirmApplyStamp(): void {
    const cardCode = this.detail?.cardCode;
    const rateId = this.selectedStampId();
    if (!cardCode || !rateId) {
      this.toast.error('กรุณาเลือกตรา');
      return;
    }
    if (!this.userId) {
      this.toast.error('ไม่พบผู้ใช้');
      return;
    }

    this.isApplyingStamp.set(true);
    this.discountApi.applyStamp({
      userId: this.userId,
      cardCode,
      parkingRateId: rateId,
      remark: this.stampRemark?.trim() || null,
    }).subscribe({
      next: (res) => {
        this.isApplyingStamp.set(false);
        const mapped = this.actionMapper.mapActionResponse(res);
        if (mapped.isSuccess) {
          this.toast.success(mapped.message || 'ให้ส่วนลดสำเร็จ');
          this.showStampModal.set(false);
          if (this.parkingTransaction?.id) {
            this.loadDetail(this.parkingTransaction.id);
          }
        } else {
          this.toast.error(mapped.message || 'ให้ส่วนลดไม่สำเร็จ');
        }
      },
      error: (err) => {
        this.isApplyingStamp.set(false);
        this.toast.error(err?.error?.message || 'ให้ส่วนลดไม่สำเร็จ');
      },
    });
  }

  openMemberCardModal() {
    if (!this.detail?.siteId || !this.detail?.cardName?.trim()) {
      this.toast.error('ไม่พบสาขาหรือชื่อบัตรสมาชิก');
      return;
    }
    this.showMemberCardModal = true;
  }

  closeMemberCardModal() {
    this.showMemberCardModal = false;
  }

  getVehicleTypeName(id: number | undefined): string {
    if (!id || !this.vehicleMaster?.cityParkingVehicleTypeListResponses) return '-';
    const type = this.vehicleMaster.cityParkingVehicleTypeListResponses.find(t => t.id === id);
    return type ? type.name : '-';
  }

  get canShowActivityHistory(): boolean {
    return !!this.parkingTransaction?.id && this.parkingTransaction.id > 0;
  }

  openActivityHistory(): void {
    if (!this.canShowActivityHistory) return;
    this.showActivityHistory = true;
  }

  closeActivityHistory(): void {
    this.showActivityHistory = false;
  }
}
